import React, { useEffect, useState } from "react";
import { allowLogin } from "jwt-storage-lib";
import { useAppDispatch, useAppSelector } from "./store/store.js";
import Loading from "./components/app/Loading/index.js";
import { resetBuildSettingsKey } from "./store/slices/buildSettingsKeys/buildSettingsKeySlice.js";
import { useGetBuildSettingsKeysQuery } from "./store/slices/buildSettingsKeys/buildSettingsKeyApi.js";
import { skipToken } from "@reduxjs/toolkit/query";
import ErrorPopup from "./components/app/Error/index.js";
import { getCurrentUserFromJwt } from "./utils/getCurrentUserFromJwt.js";
import { useInitStoreData } from "./hooks/useInitStoreData.js";
import { UserFromToken } from "./types/Users.type.js";
import { useGetCompanyQuery } from "./store/slices/dataApiSlices/companiesApiSlice.js";
import { setCompany, setUser } from "./store/slices/storeDataSlice.js";
import { useLazyGetBuildByCompanyIdQuery, useLazyGetBuildQuery } from "./store/slices/BuildsApiSlice/buildsApiSlice.js";
import Gleap from "gleap";
import * as Sentry from "@sentry/react";

type Props = {
    children: React.ReactNode;
};

function PrivatePage({ children }: Props) {
    const dispatch = useAppDispatch();
    const params = new URLSearchParams(location.search);
    const id = params.get("id");
    const onboardingNotFinished = params.has("onboard");
    const user = useAppSelector(state => state.storeData.user);
    const companyId = useAppSelector(state => state.storeData.company?._id);
    const error = useAppSelector(state => state.builder.error);
    const [loading, setLoading] = useState<boolean>(true);

    const buildCompanyId = useAppSelector(state => state.builder.build.companyId);

    //Queries
    const { data: company } = useGetCompanyQuery(user?.companies?.[0]?.$oid ? user?.companies?.[0]?.$oid : skipToken);
    const { data: buildSettingsKey, isLoading: buildSettingsKeyLoading } = useGetBuildSettingsKeysQuery(
        buildCompanyId && id ? { companyId: buildCompanyId, buildId: id } : skipToken
    );
    const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
    const [getBuild, { isLoading: getBuildLoading }] = useLazyGetBuildQuery();
    const [getBuildByCompanyId] = useLazyGetBuildByCompanyIdQuery();

    //Initialize store data
    useInitStoreData();

    //Check if user is logged in
    useEffect(() => {
        const checkLoggedIn = async () => {
            const loggedIn = await allowLogin();
            setIsLoggedIn(loggedIn);
        };
        checkLoggedIn();
    }, []);

    //From logged in user get set user
    useEffect(() => {
        if (isLoggedIn === null) return;
        const setUserFromJwt = async () => {
            const user = getCurrentUserFromJwt() as UserFromToken;
            if (onboardingNotFinished && user && !id) {
                const companyId = user?.companies?.[0]?.$oid;
                if (!companyId) return;
                const queryParams = {
                    limit: 120,
                    offset: 0,
                    isPrintBlocks: false,
                    isPublished: false
                };
                const { data: builds, isError } = await getBuildByCompanyId({ companyId: companyId, params: queryParams });
                if (isError) {
                    console.error("Error getting builds", isError);
                    return;
                }
                if (builds && builds.length > 0) {
                    const build = builds[0];
                    if (build) {
                        // TEMPORARY: Redirect to  correct onboarding flow
                        window.location.href = `${window.location.origin}/?id=${build.id}&&onboard`;
                    }
                }
            }
            dispatch(setUser(user));
            const currentScope = Sentry.getCurrentScope();
            const currentUser = currentScope.getUser() || {};
            Sentry.setUser({
                ...currentUser,
                id: user._id,
                account_id: user.organizationId || user.companies?.[0]?.$oid,
                user_type: user.type,
            });
        };

        if (isLoggedIn) {
            setUserFromJwt();
        } else if ((isLoggedIn === false || !id) && !import.meta.env.VITE_IS_DEV_LOGIN) {
            window.location.href = import.meta.env.VITE_LOGIN_URL;
        }
    }, [isLoggedIn]);

    //After user is set, company will be fetched
    useEffect(() => {
        if (company) {
            dispatch(setCompany(company));
            const currentScope = Sentry.getCurrentScope();
            const currentUser = currentScope.getUser() || {};
            Sentry.setUser({
                ...currentUser,
                company_id: company._id,
            });
        }
    }, [company]);

    useEffect(() => {
        if (user) {
            const currentScope = Sentry.getCurrentScope();
            const currentUser = currentScope.getUser() || {};
            Sentry.setUser({
                ...currentUser,
                id: user._id,
                account_id: user.organizationId || user.companies?.[0]?.$oid,
                user_type: user.type,
            });
        }
    }, [user]);

    //Load the build
    useEffect(() => {
        const loadBuilder = async () => {
            if (id && companyId) {
                getBuild({ buildId: id, companyId });
                setLoading(false);
            }
        };

        loadBuilder();
    }, [id, companyId]);

    useEffect(() => {
        if (buildSettingsKey) {
            dispatch(resetBuildSettingsKey(buildSettingsKey));
        }
    }, [buildSettingsKey]);

    useEffect(() => {
        if (user) {
            Gleap.identify(user._id, {
                email: user.email,
                name: user.firstName + " " + user.lastName,
                phone: user.phone,
                companyName: company?.name,
                companyId: company?._id
            });
        }
    }, [user]);

    if (loading || buildSettingsKeyLoading || getBuildLoading) {
        return <Loading />;
    }

    if (error) {
        return <ErrorPopup />;
    }

    // if (!id) {
    //     if (!getRealmApp().currentUser?.customData?.companies) {
    //         window.location.href = `${import.meta.env.VITE_HUB_URL}`;
    //     }
    //     return <LatestBuilds />;
    // }

    return <>{children}</>;
}

export default PrivatePage;
